(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.TimeseekFilterIndex = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const SCHEMA = 'timeseek-filter-index/v1';
  const MAX_BYTES = 2 * 1024 * 1024;
  const MAX_ITEMS = 20000;
  const requests = new Map();
  const normalize = (value) => String(value ?? '').normalize('NFKC').toLowerCase().trim().replace(/\s+/gu, ' ');
  const text = (value, max = 2000) => typeof value === 'string' && value.trim() && value.length <= max && !/[\u0000-\u001f\u007f]/u.test(value);
  const hash = (value) => typeof value === 'string' && /^[a-f0-9]{64}$/u.test(value);
  const key = (brand, reference) => JSON.stringify([normalize(brand), normalize(reference)]);
  const aliases = (item) => {
    const bilingual = item.brand.match(/^(.+?)\s*[（(]([^()（）]+)[）)]$/u);
    return [item.brand, item.brand_en, item.brand_id, ...(bilingual ? bilingual.slice(1) : [])].filter(Boolean).map(normalize);
  };

  function parse(data) {
    if (data?.schema_version !== SCHEMA || data.content_kind !== 'reference_filter_identity'
        || data.refresh_policy !== 'on_catalog_change' || data.expires_at !== null
        || data.identity_normalization !== 'nfkc_casefold_whitespace_collapse_punctuation_preserved'
        || !/^sha256-[a-f0-9]{64}$/u.test(data.generation_id)
        || data.source_bindings?.project_id !== 'timeseek-apps'
        || data.source_bindings.master_view !== 'timeseek-prd.serving.ref_master'
        || !/^sha256-[a-f0-9]{64}$/u.test(data.source_bindings.snapshot_id)
        || !hash(data.source_bindings.snapshot_manifest_sha256)
        || !hash(data.source_bindings.snapshot_sqlite_sha256)
        || !Array.isArray(data.items) || data.items.length > MAX_ITEMS
        || data.item_count !== data.items.length) return null;
    const byId = new Map(); const byIdentity = new Map();
    for (const raw of data.items) {
      if (!text(raw?.reference_id, 200) || !text(raw.canonical_ref, 200) || !text(raw.brand)
          || byId.has(raw.reference_id)) return null;
      const item = { reference_id: raw.reference_id, canonical_ref: raw.canonical_ref, brand: raw.brand };
      for (const name of ['brand_en', 'brand_id', 'model', 'model_id']) {
        if (raw[name] !== null && !text(raw[name])) return null;
        item[name] = raw[name];
      }
      const specs = {};
      for (const name of ['movement', 'case_material', 'case_diameter_mm', 'case_thickness_mm', 'lug_width_mm']) {
        const value = raw.specifications?.[name];
        if (value !== null && (name.endsWith('_mm')
          ? typeof value !== 'number' || !Number.isFinite(value) || value <= 0 : !text(value))) return null;
        specs[name] = value;
      }
      item.specifications = Object.freeze(specs); Object.freeze(item);
      byId.set(item.reference_id, item);
      for (const brand of new Set(aliases(item))) {
        const identity = key(brand, item.canonical_ref);
        const ids = byIdentity.get(identity) || new Set(); ids.add(item.reference_id);
        byIdentity.set(identity, ids);
      }
    }
    return { generationId: data.generation_id, byId, byIdentity };
  }

  // Ref punctuation is part of identity. Never extract a number from a title,
  // strip punctuation, or use an unqualified Ref to choose a master row.
  function resolve(index, dataset, contextBrand = '') {
    if (!index || !dataset) return null;
    const brands = [dataset.tsBrandId, dataset.tsBrand, contextBrand].filter((value) => normalize(value));
    const reference = dataset.tsCanonicalReference || dataset.tsReference || '';
    if (dataset.tsReferenceId) {
      const item = index.byId.get(dataset.tsReferenceId);
      if (!item || brands.some((brand) => !aliases(item).includes(normalize(brand)))
          || reference && normalize(reference) !== normalize(item.canonical_ref)) return null;
      return item;
    }
    if (!brands.length || !normalize(reference)) return null;
    let matched = null;
    for (const brand of brands) {
      const ids = index.byIdentity.get(key(brand, reference));
      if (!ids || ids.size !== 1) return null;
      const current = index.byId.get([...ids][0]);
      if (matched && matched.reference_id !== current.reference_id) return null;
      matched = current;
    }
    return matched;
  }

  function apply(item, reference) {
    if (!reference) return item;
    const specs = reference.specifications;
    const dimension = (value) => value === null ? '' : `${value} mm`;
    const explicitModel = Boolean(item.databaseDataset?.tsModel && item.model);
    return { ...item, databaseReferenceId: reference.reference_id,
      brand: reference.brand, reference: reference.canonical_ref,
      model: explicitModel ? item.model : reference.model || '',
      modelLabel: explicitModel ? item.modelLabel : reference.model || '',
      movement: specs.movement || '', caseMaterial: specs.case_material ? [specs.case_material] : [],
      caseSize: dimension(specs.case_diameter_mm), caseHeight: dimension(specs.case_thickness_mm),
      lugWidth: dimension(specs.lug_width_mm) };
  }

  function indexUrl(value, origin) {
    try {
      if (!text(value, 2048)) return null;
      const url = new URL(value, origin);
      if (url.protocol !== 'https:' || url.username || url.password || url.hash
          || ![origin, 'https://cdn.shopify.com'].includes(url.origin)
          || !url.pathname.endsWith('.json')) return null;
      return url.href;
    } catch (_) { return null; }
  }

  async function load(value, origin) {
    const url = indexUrl(value, origin);
    if (!url) return null;
    if (requests.has(url)) return requests.get(url);
    const request = (async () => {
      const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit', redirect: 'error', headers: { Accept: 'application/json' } });
        if (!response.ok || Number(response.headers.get('content-length')) > MAX_BYTES) return null;
        let raw = '';
        if (response.body?.getReader) {
          const reader = response.body.getReader(); const decoder = new TextDecoder(); let bytes = 0;
          while (true) {
            const part = await reader.read();
            if (part.done) { raw += decoder.decode(); break; }
            bytes += part.value.byteLength;
            if (bytes > MAX_BYTES) { await reader.cancel(); return null; }
            raw += decoder.decode(part.value, { stream: true });
          }
        } else {
          raw = await response.text();
          if (new TextEncoder().encode(raw).byteLength > MAX_BYTES) return null;
        }
        return parse(JSON.parse(raw));
      } catch (_) { return null; } finally { clearTimeout(timer); }
    })();
    requests.set(url, request);
    const result = await request;
    // Share in-flight failures too, but let an explicit later attempt retry.
    // Successful immutable snapshots remain cached for the lifetime of the page.
    if (!result && requests.get(url) === request) requests.delete(url);
    return result;
  }

  // Bounded parallel work preserves input order and propagates failure.
  async function pool(tasks, concurrency = 3) {
    const results = new Array(tasks.length); let next = 0;
    const count = Number.isInteger(concurrency) ? Math.max(1, Math.min(8, concurrency)) : 3;
    await Promise.all(Array.from({ length: Math.min(count, tasks.length) }, async () => {
      while (next < tasks.length) {
        const index = next++;
        results[index] = await tasks[index]();
      }
    }));
    return results;
  }
  return { parse, resolve, apply, load, pool, normalize, indexUrl };
});
